import { useIntl, FormattedMessage } from 'react-intl';
import { useTheme } from '@mui/material';
import moment from 'moment';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Tooltip from '@mui/material/Tooltip';
import Fab from '@mui/material/Fab';
import { pink } from '@mui/material/colors';

import AddIcon from '@mui/icons-material/Add';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import BackIcon from '@mui/icons-material/ArrowBack';
import PositiveIcon from '@mui/icons-material/ThumbUp';
import NegativeIcon from '@mui/icons-material/ThumbDown';
import EditIcon from '@mui/icons-material/Edit';
import TitleIcon from '@mui/icons-material/Title';

import ExpandableListItem from '~/components/ExpandableListItem';
import ContentView from '~/components/ContentView';
import { useExperience } from '~/hooks/api/useExperience';
import messages from './messages';
import styles from './styles.module.css';
import { getRouteApi, useNavigate } from '@tanstack/react-router';

const route = getRouteApi('/experiences/$experienceId/');

export const ExperienceDetails = () => {
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const { experienceId } = route.useParams();
  const experienceIdNum = parseInt(experienceId, 10);

  const { data: experience, isLoading } = useExperience({
    experienceId: experienceIdNum,
    expand: ['events', 'turn.shift', 'turn.route'],
  });

  const gotoExperienceOverview = () => {
    navigate({ to: '/experiences' });
  };

  const gotoAddEvent = () => {
    navigate({ to: `/experiences/${experienceIdNum}/add-event` });
  };

  const gotoEditEvent = (eventId: number) => {
    navigate({ to: `/experiences/${experienceIdNum}/events/${eventId}/edit` });
  };

  const gotoEditTitle = () => {
    navigate({ to: `/experiences/${experienceIdNum}/edit-title` });
  };

  const turn = experience?.turn;

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <IconButton
              onClick={gotoExperienceOverview}
              color="inherit"
              size="large"
              aria-label={intl.formatMessage(messages.back)}
            >
              <BackIcon />
            </IconButton>
            <Typography variant="h6" style={{ marginLeft: theme.spacing(2) }}>
              <FormattedMessage {...messages.detailsHeader} />
            </Typography>
          </Toolbar>
        </AppBar>
      }
      floatingActionButton={
        <Fab
          color="secondary"
          onClick={gotoAddEvent}
          aria-label={intl.formatMessage(messages.createEventHeader)}
        >
          <AddIcon />
        </Fab>
      }
      isLoading={isLoading}
    >
      <List>
        <ListSubheader>
          <FormattedMessage {...messages.generalSection} />
        </ListSubheader>
        <ListItem
          secondaryAction={
            <Tooltip
              title={intl.formatMessage(messages.editExperienceTitleTooltip)}
            >
              <IconButton onClick={gotoEditTitle} size="large">
                <EditIcon />
              </IconButton>
            </Tooltip>
          }
        >
          <ListItemIcon>
            <TitleIcon />
          </ListItemIcon>
          <ListItemText
            primary={experience?.title}
            secondary={intl.formatMessage(messages.titleLabel)}
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <CalendarIcon />
          </ListItemIcon>
          <ListItemText
            primary={
              turn
                ? `${moment(turn.shift?.startDate).format('LLL')} - ${turn.route?.name}`
                : intl.formatMessage(messages.outsideOfShift)
            }
            secondary={intl.formatMessage(messages.selectTurnLabel)}
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            {experience?.isPositive ? <PositiveIcon /> : <NegativeIcon />}
          </ListItemIcon>
          <ListItemText
            primary={
              experience?.isPositive
                ? intl.formatMessage(messages.positive)
                : intl.formatMessage(messages.negative)
            }
            secondary={intl.formatMessage(messages.positiveLabel)}
          />
        </ListItem>
        <Divider />
      </List>
      <List className={styles['event-list']}>
        <ListSubheader className={styles.subheader}>
          <FormattedMessage {...messages.eventsSection} />
        </ListSubheader>
        {experience?.events?.map((event, index) => (
          <ExpandableListItem
            key={event.id}
            primaryLabel={moment(event.eventDate).format('LLL')}
            secondaryLabel={event.location}
            icon={
              <Avatar
                style={{ backgroundColor: 'transparent', color: pink.A200 }}
              >
                # {index + 1}
              </Avatar>
            }
            open={false}
          >
            <ListItem
              key={`${event.id}-desc`}
              secondaryAction={
                <Tooltip title={intl.formatMessage(messages.editEventHeader)}>
                  <IconButton
                    onClick={() => {
                      gotoEditEvent(event.id ?? 0);
                    }}
                    size="large"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemText
                primary={
                  <div className={styles.descriptionText}>
                    {event.description}
                  </div>
                }
              />
            </ListItem>
          </ExpandableListItem>
        ))}
      </List>
    </ContentView>
  );
};

export default ExperienceDetails;
